import { useGetUser } from "@hooks";
import { Document, DocumentVersion } from "@models";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { ArrowDropDown } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { UPGRADE_PLAN_TOOLTIP } from "src/utils/consts";
import {
  getDocVersionRevisionNumber,
  getDocVersionsWithStatus,
} from "../../../../../utils";
import { checkVersionHasChanges } from "../../helpers";
import SelectableOption from "./SelectableOption";

const DocVersionDropDown = ({
  selectedVersion,
  onSelectDocVersion,
  isDocEnabled = true,
  isMultiInstanceDoc = false,
  document,
  isDiffMode = false,
}: {
  selectedVersion: DocumentVersion;
  onSelectDocVersion?: (doc: DocumentVersion) => void;
  isDocEnabled?: boolean;
  isMultiInstanceDoc?: boolean;
  document: Document;
  isDiffMode?: boolean;
}) => {
  const { data: user } = useGetUser();
  const { orgId = "" } = useParams();
  const [docVersionDropdownOpen, setDocVersionDropdownOpen] = useState(false);

  if (!user) return null;

  const docVersionsWithStatus = getDocVersionsWithStatus({
    document,
    user,
    orgId,
  });

  return (
    <Tooltip
      title={UPGRADE_PLAN_TOOLTIP}
      {...(isDocEnabled && { open: false })}
    >
      <div>
        <ClickAwayListener
          onClickAway={() => isDocEnabled && setDocVersionDropdownOpen(false)}
        >
          <div
            className={`relative ${docVersionDropdownOpen ? "z-20" : ""}`}
            data-testid="doc-version-dropdown"
          >
            <div
              className={`p-x-2 flex ${!isDocEnabled ? "pointer-events-none" : "cursor-pointer"} items-center justify-between ${isMultiInstanceDoc ? "rounded-r-md" : "rounded-md"} border border-solid border-gray-300 px-2 py-1.5 hover:bg-zinc-100`}
              onClick={() => {
                setDocVersionDropdownOpen((prev) => !prev);
              }}
            >
              <SelectableOption
                label={`Revision ${getDocVersionRevisionNumber(
                  selectedVersion,
                  document.versions
                )}`}
                type={
                  isDiffMode && checkVersionHasChanges(selectedVersion)
                    ? "CHANGES"
                    : docVersionsWithStatus.find(
                        (v) => v.id === selectedVersion.id
                      )?.status
                }
                showStatus
                disabled={
                  !isDocEnabled ||
                  (isDiffMode && !checkVersionHasChanges(selectedVersion))
                }
              />
              <ArrowDropDown fontSize="small" />
            </div>
            <div
              className={`mt-1 flex  min-w-max flex-col overflow-auto rounded-md border border-solid border-slate-300 bg-color-bg shadow-md ${docVersionDropdownOpen ? "absolute" : "hidden"}`}
            >
              {isDocEnabled &&
                docVersionsWithStatus
                  .sort((a, b) => (a.revision < b.revision ? 1 : -1))
                  .filter((version) => version.status !== "AVAILABLE")
                  .map((version) => {
                    const { showChangesChip, isDisabled } =
                      getDiffModeBehaviorForVersion(version, isDiffMode);

                    const displayDate =
                      showChangesChip && version.changesFile?.createdAt
                        ? version.changesFile.createdAt
                        : version.createdAt;

                    const dropdownItem = (
                      <div
                        className={`flex items-center p-2 ${
                          isDisabled
                            ? "cursor-default"
                            : "cursor-pointer hover:bg-slate-100"
                        } ${selectedVersion.id === version.id && "bg-slate-200"}`}
                        onClick={() => {
                          if (isDisabled) return;
                          onSelectDocVersion && onSelectDocVersion(version);
                          setDocVersionDropdownOpen((prev) => !prev);
                        }}
                      >
                        <SelectableOption
                          label={`Revision ${version.revision} - ${moment(displayDate).fromNow()}`}
                          type={showChangesChip ? "CHANGES" : version.status}
                          showStatus={!isDisabled}
                          disabled={isDisabled}
                        />
                      </div>
                    );

                    if (isDisabled) {
                      return (
                        <Tooltip
                          key={`${version.id}-selectable-option`}
                          title="No Changes File"
                          placement="left"
                        >
                          {dropdownItem}
                        </Tooltip>
                      );
                    }

                    return (
                      <div key={`${version.id}-selectable-option`}>
                        {dropdownItem}
                      </div>
                    );
                  })}
            </div>
          </div>
        </ClickAwayListener>
      </div>
    </Tooltip>
  );
};

const getDiffModeBehaviorForVersion = (
  version: DocumentVersion,
  isDiffMode: boolean
) => {
  const hasChangesFile = checkVersionHasChanges(version);

  const showChangesChip = isDiffMode && hasChangesFile;
  const isDisabled = isDiffMode && !hasChangesFile;
  return { showChangesChip, isDisabled };
};

export default DocVersionDropDown;
